import React from 'react';
import { useAuth } from '../auth/AuthProvider';
import { FaStopwatch, FaChartLine, FaMobileAlt, FaChevronDown, FaFileDownload, FaTrophy } from 'react-icons/fa';
import { GiHorseHead } from 'react-icons/gi';

const Landing = () => {
  const { user } = useAuth();

  const handleNavigation = (path) => {
    window.location.href = path;
  };

  // Brand colors from the logo
  const brandColors = {
    primary: '#29458d',    // Dark blue
    secondary: '#dbc078',  // Gold
    white: '#ffffff',
    lightBlue: '#e8edf7'   // Light version of primary for backgrounds
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: brandColors.white
    }}>
      <main style={{
        position: 'relative'
      }}>
        <section className="hero-section" style={{
          position: 'relative',
          minHeight: '45vh',
          display: 'flex',
          alignItems: 'center',
          background: brandColors.lightBlue,
          overflow: 'hidden',
          paddingTop: '0.5rem',  // Reduced from 1rem
          paddingBottom: '1rem'
        }}>
          <div className="hero-content" style={{
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '1.5rem',
            textAlign: 'center',
            position: 'relative'
          }}>
            <GiHorseHead style={{
              fontSize: '4rem',
              color: brandColors.primary,
              marginBottom: '1rem',
              opacity: 0.8
            }} />
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: '800',
              color: brandColors.primary,
              marginBottom: '0.75rem',
              lineHeight: '1.1',
              letterSpacing: '-0.02em'
            }}>
              Pre&nbsp;&nbsp;&nbsp;Go&nbsp;&nbsp;&nbsp;Prep
            </h1>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
              fontWeight: '600',
              color: brandColors.primary,
              marginBottom: '1rem',
              opacity: 0.9,
              letterSpacing: '-0.01em'
            }}>
              Barrel Racing Performance Timing & Preparation
            </h2>
            <p style={{
              fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
              color: brandColors.primary,
              maxWidth: '800px',
              margin: '0 auto 1.5rem',
              lineHeight: '1.5',
              opacity: 0.8
            }}>
              Perfect your timing. Optimize your performance.
            </p>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <div style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}>
                {!user && (
                  <button 
                    onClick={() => handleNavigation('/signup')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '1rem 2rem',
                      backgroundColor: brandColors.primary,
                      color: brandColors.white,
                      border: 'none',
                      borderRadius: '12px',
                      fontWeight: '600',
                      fontSize: '1.1rem',
                      cursor: 'pointer',
                      boxShadow: '0 4px 6px rgba(41, 69, 141, 0.2)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 12px rgba(41, 69, 141, 0.25)'
                      }
                    }}
                  >
                    Get Started for Free
                  </button>
                )}
              </div>
              
              <div style={{
                marginTop: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                color: brandColors.primary,
                opacity: 0.6
              }}>
                <FaChevronDown style={{ fontSize: '1.5rem', animation: 'bounce 2s infinite' }} />
                <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Scroll to learn more</span>
              </div>
            </div>
          </div>
        </section>
        
        {/* Add keyframe animation for the bounce effect */}
        <style>
          {`
            @keyframes bounce {
              0%, 20%, 50%, 80%, 100% {
                transform: translateY(0);
              }
              40% {
                transform: translateY(-10px);
              }
              60% {
                transform: translateY(-5px);
              }
            }
          `}
        </style>

        <section className="section" style={{
          padding: '2rem 1rem',
          background: brandColors.white
        }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
            fontWeight: '700',
            color: brandColors.primary,
            textAlign: 'center',
            marginBottom: '3rem',
            letterSpacing: '-0.02em'
          }}>
            How&nbsp;&nbsp;It&nbsp;&nbsp;Works
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}>
            {/* Step 1 */}
            <div style={{
              background: brandColors.white,
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 4px 20px rgba(41, 69, 141, 0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(41, 69, 141, 0.12)'
              }
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${brandColors.primary}, ${brandColors.primary}dd)`,
                  color: brandColors.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginRight: '1rem'
                }}>
                  <GiHorseHead style={{ transform: 'scale(1.4)' }} />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: brandColors.primary,
                  margin: 0
                }}>
                  Enter Horse Data
                </h3>
              </div>
              <p style={{
                fontSize: '1.1rem',
                color: brandColors.primary,
                opacity: 0.8,
                lineHeight: '1.6',
                margin: 0,
                textAlign: 'center'
              }}>
                Input draw numbers, estimated runs per hour, therapeutic application schedules, break times, and other essential information for your horses
              </p>
            </div>

            {/* Step 2 */}
            <div style={{
              background: brandColors.white,
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 4px 20px rgba(41, 69, 141, 0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(41, 69, 141, 0.12)'
              }
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${brandColors.primary}, ${brandColors.primary}dd)`,
                  color: brandColors.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginRight: '1rem'
                }}>
                  <FaFileDownload />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: brandColors.primary,
                  margin: 0
                }}>
                  Generate Results
                </h3>
              </div>
              <p style={{
                fontSize: '1.1rem',
                color: brandColors.primary,
                opacity: 0.8,
                lineHeight: '1.6',
                margin: 0,
                textAlign: 'center'
              }}>
       Get optimized timing and therapeutic administration schedules calculated automatically. Receive text messages or download your results for quick and easy access anywhere              </p>
            </div>

            {/* Step 3 */}
            <div style={{
              background: brandColors.white,
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 4px 20px rgba(41, 69, 141, 0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(41, 69, 141, 0.12)'
              }
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${brandColors.primary}, ${brandColors.primary}dd)`,
                  color: brandColors.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginRight: '1rem'
                }}>
                  <FaTrophy />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: brandColors.primary,
                  margin: 0
                }}>
                  Improve Performance
                </h3>
              </div>
              <p style={{
                fontSize: '1.1rem',
                color: brandColors.primary,
                opacity: 0.8,
                lineHeight: '1.6',
                margin: 0,
                textAlign: 'center'
              }}>
                Reduce errors and optimize horse management with precise timing and scheduling recommendations
              </p>
            </div>
          </div>
        </section>

        <section className="section" style={{
          background: brandColors.lightBlue,
          padding: '2rem 1rem 4rem 1rem'
        }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
            fontWeight: '700',
            color: brandColors.primary,
            textAlign: 'center',
            marginBottom: '3rem',
            letterSpacing: '-0.02em'
          }}>
            Key&nbsp;&nbsp;Benefits
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}>
            <div style={{
              background: brandColors.white,
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 6px 25px rgba(41, 69, 141, 0.15)',  // Increased shadow
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(41, 69, 141, 0.12)'
              }
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${brandColors.primary}, ${brandColors.primary}dd)`,
                color: brandColors.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                marginBottom: '1.5rem',
                marginInline: 'auto'
              }}>
                <FaStopwatch />
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: brandColors.primary,
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                Real-Time Precision
              </h3>
              <p style={{
                fontSize: '1.1rem',
                color: brandColors.primary,
                opacity: 0.8,
                lineHeight: '1.6',
                margin: 0,
                textAlign: 'center'
              }}>
                Accurately estimate Go times and therapeutic tool administration for optimal performance
              </p>
            </div>

            <div style={{
              background: brandColors.white,
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 4px 20px rgba(41, 69, 141, 0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(41, 69, 141, 0.12)'
              }
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${brandColors.primary}, ${brandColors.primary}dd)`,
                color: brandColors.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                marginBottom: '1.5rem',
                marginInline: 'auto'
              }}>
                <FaChartLine />
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: brandColors.primary,
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                User-Friendly Interface
              </h3>
              <p style={{
                fontSize: '1.1rem',
                color: brandColors.primary,
                opacity: 0.8,
                lineHeight: '1.6',
                margin: 0,
                textAlign: 'center'
              }}>
                Designed for simplicity and ease of use, making calculations quick and hassle-free
              </p>
            </div>

            <div style={{
              background: brandColors.white,
              borderRadius: '16px',
              padding: '2rem',
              boxShadow: '0 4px 20px rgba(41, 69, 141, 0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(41, 69, 141, 0.12)'
              }
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${brandColors.primary}, ${brandColors.primary}dd)`,
                color: brandColors.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                marginBottom: '1.5rem',
                marginInline: 'auto'
              }}>
                <FaMobileAlt />
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: brandColors.primary,
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                Mobile Friendly
              </h3>
              <p style={{
                fontSize: '1.1rem',
                color: brandColors.primary,
                opacity: 0.8,
                lineHeight: '1.6',
                margin: 0,
                textAlign: 'center'
              }}>
                Access your data anywhere, anytime
              </p>
            </div>
          </div>
        </section>

        <section style={{ 
          textAlign: 'center', 
          padding: '4rem 1rem 2rem 1rem',  // Changed bottom padding from 4rem to 2rem
          background: brandColors.white,
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Abstract Wave Background */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: `
              radial-gradient(circle at 30% 20%, ${brandColors.secondary} 0%, transparent 50%),
              radial-gradient(circle at 70% 60%, ${brandColors.primary} 0%, transparent 40%)
            `,
            transform: 'scale(1.1)'
          }} />

          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 1
          }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.5rem)',
              fontWeight: '700',
              color: brandColors.primary,
              marginBottom: '1.5rem',
              letterSpacing: '-0.02em'
            }}>
              Ready to Optimize Your Performance?
            </h2>
            <p style={{
              fontSize: '1.25rem',
              marginBottom: '2rem',
              color: brandColors.primary,
              opacity: 0.8,
              lineHeight: 1.6
            }}>
              Join barrel racers who are improving their precision and efficiency with Pre Go Prep
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              alignItems: 'center',
              margin: '2rem 0',
              width: '100%',
              padding: '0 1rem'
            }}>
              <button 
                onClick={() => handleNavigation(user ? '/calculations' : '/signup')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1rem 2rem',
                  backgroundColor: brandColors.primary,
                  color: brandColors.white,
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 20px rgba(41, 69, 141, 0.15)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  marginBottom: '1rem',
                  width: '100%',
                  maxWidth: '300px'
                }}
              >
                {user ? "Go to Calculator" : "Get Started for Free →"}
              </button>
              <button 
                onClick={() => handleNavigation('/about-contact#contact')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1rem 2rem',
                  backgroundColor: 'transparent',
                  color: brandColors.primary,
                  border: '2px solid ' + brandColors.primary,
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  maxWidth: '300px'
                }}
              >
                Contact Us
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Landing;